"use client";

import * as React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey, $getSelection, $isParagraphNode, $isRangeSelection, type LexicalNode } from "lexical";
import { $isListItemNode } from "@lexical/list";
import { $createHeadingNode } from "@lexical/rich-text";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import {
  IconH1,
  IconH2,
  IconH3,
  IconH4,
  IconH5,
  IconH6,
  IconList,
  IconListNumbers,
  IconPhoto,
  IconPilcrow,
  IconTable,
} from "@tabler/icons-react";
import { getHeadingLevelLabel } from "../../kernel/constants";
import { findClosestBlockNodeKey } from "../../kernel/helpers/findClosestBlockNodeKey";
import { $replaceParagraphWithImageBlock } from "../../kernel/nodes/ImageBlockNode";
import { slashMenuCancelledUntilNextSlashRef } from "./slashMenuCancelledRef";

type SlashItemKind = "paragraph" | "heading" | "ul" | "ol" | "table" | "image" | "disabled";

type SlashItem = {
  id: string;
  label: string;
  kind: SlashItemKind;
  level?: number;
  disabled?: boolean;
  icon: React.ComponentType<{ className?: string }>;
};

/** 标签子串 + id 前缀（避免 `paragraph` 含字母 `h` 被 `id.includes("h")` 误命中） */
function itemMatchesSlashQuery(it: SlashItem, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;
  if (normalizedQuery === "/") return true;
  const label = it.label.toLowerCase();
  const id = it.id.toLowerCase();
  if (label.includes(normalizedQuery)) return true;
  return id.startsWith(normalizedQuery);
}

const SLASH_MENU_GAP_PX = 6;
const SLASH_MENU_VIEWPORT_PAD_PX = 8;
const SLASH_MENU_WIDTH_PX = 192; // w-48
const SLASH_MENU_HEADER_EST_PX = 44;
const SLASH_MENU_ROW_EST_PX = 40;

type SlashMenuAnchor = { top: number; bottom: number; left: number };

type SlashMenuFloatingStyle = { top: number; left: number; maxHeight: number };

function clampSlashMenuLeft(left: number, vw: number): number {
  const pad = SLASH_MENU_VIEWPORT_PAD_PX;
  const menuW = SLASH_MENU_WIDTH_PX;
  let l = left;
  if (l + menuW > vw - pad) l = vw - menuW - pad;
  if (l < pad) l = pad;
  return l;
}

/**
 * 视口内定位：优先在段落下方；空间不足时翻到上方；上方时菜单底边不越过段落顶之上 gap（不挡当前行）。
 */
function computeSlashMenuFloating(
  anchor: SlashMenuAnchor,
  contentHeight: number,
  vw: number,
  vh: number,
): SlashMenuFloatingStyle {
  const gap = SLASH_MENU_GAP_PX;
  const pad = SLASH_MENU_VIEWPORT_PAD_PX;
  const maxCap = Math.min(320, vh * 0.5);
  const spaceBelow = Math.max(0, vh - anchor.bottom - gap - pad);
  const spaceAbove = Math.max(0, anchor.top - gap - pad);

  let placement: "below" | "above";
  let maxH: number;

  if (contentHeight <= spaceBelow) {
    placement = "below";
    maxH = Math.min(maxCap, contentHeight);
  } else if (contentHeight <= spaceAbove) {
    placement = "above";
    maxH = Math.min(maxCap, contentHeight);
  } else if (spaceAbove > spaceBelow) {
    placement = "above";
    maxH = Math.min(maxCap, spaceAbove);
  } else {
    placement = "below";
    maxH = Math.min(maxCap, spaceBelow);
  }

  const maxAboveNonOverlap = anchor.top - gap - pad;
  if (placement === "above" && maxAboveNonOverlap < 32) {
    placement = "below";
    maxH = Math.min(maxCap, contentHeight, spaceBelow);
  }

  if (placement === "below") {
    const top = anchor.bottom + gap;
    const h = Math.min(contentHeight, maxH);
    if (top + h > vh - pad) {
      maxH = Math.max(32, Math.min(maxH, vh - pad - top));
    }
    return { top, left: clampSlashMenuLeft(anchor.left, vw), maxHeight: maxH };
  }

  // 上方：菜单底边在段落顶之上 gap，顶边不低于 pad
  maxH = Math.min(maxH, maxAboveNonOverlap);
  const h = Math.min(contentHeight, maxH);
  const top = anchor.top - gap - h;
  return { top, left: clampSlashMenuLeft(anchor.left, vw), maxHeight: maxH };
}

/** 须在 `editor.getEditorState().read` 内调用（不要求折叠选区，避免偶发选区态导致菜单键盘失效） */
function $isSlashCommandParagraphMode(): boolean {
  if (slashMenuCancelledUntilNextSlashRef.current) return false;
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;

  const key = findClosestBlockNodeKey(selection.anchor.getNode());
  if (!key) return false;

  let blockOrPara: LexicalNode | null = $getNodeByKey(key);
  if ($isListItemNode(blockOrPara)) {
    let inner: LexicalNode | null = null;
    for (const c of blockOrPara.getChildren()) {
      if ($isParagraphNode(c)) {
        inner = c;
        break;
      }
    }
    blockOrPara = inner;
  }
  if (!$isParagraphNode(blockOrPara)) return false;
  return blockOrPara.getTextContent().startsWith("/");
}

export function SlashMenuPlugin({
  isEditable,
  containerRef: _containerRef,
}: {
  isEditable: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [editor] = useLexicalComposerContext();

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [menuAnchor, setMenuAnchor] = React.useState<SlashMenuAnchor | null>(null);
  const [menuFloating, setMenuFloating] = React.useState<SlashMenuFloatingStyle | null>(null);
  const [targetParagraphKey, setTargetParagraphKey] = React.useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);

  const menuPanelRef = React.useRef<HTMLDivElement | null>(null);
  const itemButtonRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const items = React.useMemo<SlashItem[]>(
    () => [
      { id: "paragraph", label: "段落", kind: "paragraph", icon: IconPilcrow },
      { id: "h1", label: getHeadingLevelLabel(1), kind: "heading", level: 1, icon: IconH1 },
      { id: "h2", label: getHeadingLevelLabel(2), kind: "heading", level: 2, icon: IconH2 },
      { id: "h3", label: getHeadingLevelLabel(3), kind: "heading", level: 3, icon: IconH3 },
      { id: "h4", label: getHeadingLevelLabel(4), kind: "heading", level: 4, icon: IconH4 },
      { id: "h5", label: getHeadingLevelLabel(5), kind: "heading", level: 5, icon: IconH5 },
      { id: "h6", label: getHeadingLevelLabel(6), kind: "heading", level: 6, icon: IconH6 },
      { id: "ul", label: "无序列表", kind: "ul", icon: IconList },
      { id: "ol", label: "有序列表", kind: "ol", icon: IconListNumbers },
      { id: "table", label: "普通表格", kind: "table", icon: IconTable },
      { id: "image", label: "图片", kind: "image", icon: IconPhoto },
    ],
    [],
  );

  React.useEffect(() => {
    if (!isEditable) {
      setOpen(false);
      setMenuAnchor(null);
      setMenuFloating(null);
      return;
    }

    let raf = 0;
    const unregister = editor.registerUpdateListener(() => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          const key = findClosestBlockNodeKey(selection.anchor.getNode());
          if (!key) return;

          let blockOrPara: LexicalNode | null = $getNodeByKey(key);
          if ($isListItemNode(blockOrPara)) {
            let inner: LexicalNode | null = null;
            for (const c of blockOrPara.getChildren()) {
              if ($isParagraphNode(c)) {
                inner = c;
                break;
              }
            }
            blockOrPara = inner;
          }
          if (!$isParagraphNode(blockOrPara)) return;

          const paragraphKey = blockOrPara.getKey();
          const text = blockOrPara.getTextContent();
          if (!text.startsWith("/")) {
            slashMenuCancelledUntilNextSlashRef.current = false;
            setOpen(false);
            setQuery("");
            setMenuAnchor(null);
            setMenuFloating(null);
            setTargetParagraphKey(null);
            return;
          }

          if (slashMenuCancelledUntilNextSlashRef.current) {
            setOpen(false);
            setMenuAnchor(null);
            setMenuFloating(null);
            return;
          }

          const q = text.slice(1);
          const el = editor.getElementByKey(paragraphKey);
          if (!el) return;

          const eRect = el.getBoundingClientRect();
          setMenuAnchor((prev) => {
            const next: SlashMenuAnchor = {
              top: eRect.top,
              bottom: eRect.bottom,
              left: eRect.left,
            };
            if (
              prev &&
              Math.abs(prev.top - next.top) < 1 &&
              Math.abs(prev.bottom - next.bottom) < 1 &&
              Math.abs(prev.left - next.left) < 1
            ) {
              return prev;
            }
            return next;
          });
          setTargetParagraphKey(paragraphKey);
          setQuery(q);
          setOpen(true);
        });
      });
    });

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      unregister();
    };
  }, [editor, isEditable]);

  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest("[data-slash-menu]")) return;
      if (t.closest("[data-add-block-plus]")) return;
      if (t.closest('[data-floating-root="true"]')) return;
      if (
        t.closest(
          '[data-slot="dropdown-menu"], [data-slot="dropdown-menu-portal"], [data-slot="dropdown-menu-positioner"], [data-slot="dropdown-trigger"], [data-slot="dropdown-submenu-trigger"]',
        )
      )
        return;
      if (
        t.closest(
          '[data-slot="popover-root"], [data-slot="popover-trigger"], [data-slot="popover-dialog"], [data-slot="popover-content"]',
        )
      )
        return;
      if (t.closest('[data-slot="tooltip-root"], [data-slot="tooltip-trigger"], [role="tooltip"]')) return;
      if (t.closest('[data-slot="dialog-content"], [data-slot="dialog-overlay"], [data-image-crop-dialog="true"]')) return;
      slashMenuCancelledUntilNextSlashRef.current = true;
      setOpen(false);
      setMenuAnchor(null);
      setMenuFloating(null);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleItems = React.useMemo(() => {
    if (!normalizedQuery) return items;
    const q = normalizedQuery;
    const filtered = items.filter((it) => itemMatchesSlashQuery(it, q));
    const order = new Map(items.map((it, i) => [it.id, i]));
    return [...filtered].sort((a, b) => {
      const ida = a.id.toLowerCase().startsWith(q) ? 0 : 1;
      const idb = b.id.toLowerCase().startsWith(q) ? 0 : 1;
      if (ida !== idb) return ida - idb;
      return (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
    });
  }, [items, normalizedQuery]);

  React.useLayoutEffect(() => {
    if (!open || !menuAnchor) {
      setMenuFloating(null);
      return;
    }

    const vv = window.visualViewport;

    const sync = () => {
      const el = menuPanelRef.current;
      const vw = vv?.width ?? window.innerWidth;
      const vh = vv?.height ?? window.innerHeight;
      const ch = el
        ? Math.ceil(el.scrollHeight)
        : SLASH_MENU_HEADER_EST_PX + visibleItems.length * SLASH_MENU_ROW_EST_PX;
      setMenuFloating(computeSlashMenuFloating(menuAnchor, ch, vw, vh));
    };

    sync();
    const id = window.requestAnimationFrame(sync);
    window.addEventListener("resize", sync);
    vv?.addEventListener("resize", sync);
    vv?.addEventListener("scroll", sync);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener("resize", sync);
      vv?.removeEventListener("resize", sync);
      vv?.removeEventListener("scroll", sync);
    };
  }, [open, menuAnchor, visibleItems.length, normalizedQuery]);

  const visibleItemsRef = React.useRef(visibleItems);
  visibleItemsRef.current = visibleItems;
  const highlightedIndexRef = React.useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;

  const prevNormalizedQueryRef = React.useRef<string | null>(null);

  /** 过滤变化 / 刚打开 → 高亮第一项；否则仅夹紧到当前列表范围 */
  React.useLayoutEffect(() => {
    if (!open) {
      prevNormalizedQueryRef.current = null;
      return;
    }
    const max = Math.max(0, visibleItems.length - 1);
    const prev = prevNormalizedQueryRef.current;
    if (prev === null || prev !== normalizedQuery) {
      prevNormalizedQueryRef.current = normalizedQuery;
      highlightedIndexRef.current = 0;
      setHighlightedIndex(0);
      return;
    }
    setHighlightedIndex((i) => {
      const next = Math.min(Math.max(0, i), max);
      highlightedIndexRef.current = next;
      return next;
    });
  }, [open, normalizedQuery, visibleItems.length]);

  const apply = React.useCallback(
    (kind: SlashItemKind, level?: number) => {
      if (!targetParagraphKey) return;
      if (!isEditable) return;

      setOpen(false);
      setMenuAnchor(null);
      setMenuFloating(null);

      editor.update(() => {
        const node = $getNodeByKey(targetParagraphKey);
        if ($isParagraphNode(node)) {
          node.clear();
          if (kind === "paragraph") return;

          if (kind === "image") {
            $replaceParagraphWithImageBlock(targetParagraphKey, true);
            return;
          }

          if (kind === "heading" && level) {
            const tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
            const heading = $createHeadingNode(tag);
            node.replace(heading);
            heading.selectStart();
            return;
          }

          if (kind === "table") {
            (node as any).selectStart?.();
            return;
          }
        }
      });

      if (kind === "ul") editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      if (kind === "ol") editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      if (kind === "table") editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns: "3", rows: "3", includeHeaders: false });
      editor.focus();
    },
    [editor, isEditable, targetParagraphKey],
  );

  const activateItem = React.useCallback(
    (it: SlashItem | undefined) => {
      if (!it || it.disabled) return;
      if (it.kind === "heading" && it.level != null) apply(it.kind, it.level);
      else if (it.kind !== "disabled") apply(it.kind);
    },
    [apply],
  );

  const activateItemRef = React.useRef(activateItem);
  activateItemRef.current = activateItem;

  /**
   * 挂在 **Lexical 根元素 capture**：早于内部 contenteditable 默认行为与块导航，
   * 且每次按键用 `read()` 判断斜杠模式（不依赖 ref 与 update 的帧序）。
   */
  const onSlashMenuKeyDownCapture = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.isComposing) return;
      let slash = false;
      editor.getEditorState().read(() => {
        slash = $isSlashCommandParagraphMode();
      });
      if (!slash) return;

      const vis = visibleItemsRef.current;
      const n = vis.length;

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        slashMenuCancelledUntilNextSlashRef.current = true;
        setOpen(false);
        setMenuAnchor(null);
        setMenuFloating(null);
        editor.focus();
        return;
      }

      if (n === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        const i = highlightedIndexRef.current;
        const next = Math.min(i + 1, n - 1);
        highlightedIndexRef.current = next;
        setHighlightedIndex(next);
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        const i = highlightedIndexRef.current;
        const next = Math.max(i - 1, 0);
        highlightedIndexRef.current = next;
        setHighlightedIndex(next);
        return;
      }

      if (e.key === "Home") {
        e.preventDefault();
        e.stopPropagation();
        highlightedIndexRef.current = 0;
        setHighlightedIndex(0);
        return;
      }

      if (e.key === "End") {
        e.preventDefault();
        e.stopPropagation();
        const last = n - 1;
        highlightedIndexRef.current = last;
        setHighlightedIndex(last);
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        const it = vis[highlightedIndexRef.current];
        activateItemRef.current(it);
      }
    },
    [editor],
  );

  React.useEffect(() => {
    if (!isEditable) return;
    return editor.registerRootListener((rootEl: HTMLElement | null, prevRoot: HTMLElement | null) => {
      if (prevRoot) prevRoot.removeEventListener("keydown", onSlashMenuKeyDownCapture, true);
      if (rootEl) rootEl.addEventListener("keydown", onSlashMenuKeyDownCapture, true);
    });
  }, [editor, isEditable, onSlashMenuKeyDownCapture]);

  React.useLayoutEffect(() => {
    if (!open || visibleItems.length === 0) return;
    const el = itemButtonRefs.current[highlightedIndex];
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, open, visibleItems.length]);

  const onSlashMenuAction = React.useCallback(
    (id: string) => {
      const it = visibleItems.find((x) => x.id === id);
      activateItem(it);
    },
    [activateItem, visibleItems],
  );

  if (!open || !menuAnchor) return null;

  itemButtonRefs.current.length = visibleItems.length;

  const vwFallback = typeof window !== "undefined" ? window.visualViewport?.width ?? window.innerWidth : 400;
  const vhFallback = typeof window !== "undefined" ? window.visualViewport?.height ?? window.innerHeight : 640;
  const floating =
    menuFloating ?? {
      top: menuAnchor.bottom + SLASH_MENU_GAP_PX,
      left: clampSlashMenuLeft(menuAnchor.left, vwFallback),
      maxHeight: Math.min(320, vhFallback * 0.5),
    };

  return (
    <div
      ref={menuPanelRef}
      data-slash-menu
      className="fixed z-[220] flex w-48 flex-col overflow-y-auto rounded-lg border border-divider bg-surface py-1 text-sm text-surface-foreground shadow-lg outline-none"
      style={{ top: floating.top, left: floating.left, maxHeight: floating.maxHeight }}
      role="presentation"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="pointer-events-none shrink-0 border-b border-divider px-3 py-2 text-xs font-normal">
        <span className="text-surface-foreground opacity-70">/{query.trim()}</span>
      </div>
      {visibleItems.map((it, index) => {
        const Icon = it.icon;
        const disabled = Boolean(it.disabled);
        const highlighted = index === highlightedIndex;
        return (
          <button
            key={it.id}
            id={`slash-menu-item-${it.id}`}
            ref={(el) => {
              itemButtonRefs.current[index] = el;
            }}
            type="button"
            tabIndex={-1}
            disabled={disabled}
            data-slash-menu-item-highlighted={highlighted ? "true" : undefined}
            className={`flex w-full shrink-0 items-center gap-2 px-3 py-2 text-left text-surface-foreground disabled:cursor-not-allowed disabled:opacity-40 ${
              highlighted
                ? "bg-surface-secondary font-medium text-surface-secondary-foreground"
                : "hover:bg-surface-secondary"
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setHighlightedIndex(index)}
            onClick={() => onSlashMenuAction(it.id)}
          >
            <Icon className="h-4 w-4 shrink-0 opacity-80" />
            <span>{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}
